import { useEffect, useState } from "react";
import {
  createEmptyPart,
  maintenanceServiceTypes,
  performedByOptions,
  normalizePartsUsed
} from "../utils/maintenance";

const emptyForm = {
  serviceType: "Oil Change",
  serviceDate: "",
  mileage: "",
  partsUsed: [createEmptyPart()],
  laborCost: "",
  serviceProvider: "",
  performedBy: "Owner",
  nextServiceMileage: "",
  nextServiceDate: "",
  receiptUrl: "",
  notes: ""
};

function AddMaintenanceModal({
  isOpen,
  editingMaintenanceId,
  initialMaintenance,
  onSave,
  onCancel,
  isSaving = false
}) {
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    if (initialMaintenance) {
      setForm({
        serviceType: initialMaintenance.serviceType || "Oil Change",
        serviceDate: initialMaintenance.serviceDate || initialMaintenance.date || "",
        mileage: initialMaintenance.mileage ?? "",
        partsUsed:
          initialMaintenance.partsUsed?.length > 0
            ? initialMaintenance.partsUsed.map((part) => ({
                id: part.id || createEmptyPart().id,
                partName: part.partName || part.part || "",
                brand: part.brand || part.partBrand || "",
                partNumber: part.partNumber || "",
                cost: part.cost ?? ""
              }))
            : [createEmptyPart()],
        laborCost: initialMaintenance.laborCost ?? "",
        serviceProvider:
          initialMaintenance.serviceProvider || initialMaintenance.shopName || "",
        performedBy: initialMaintenance.performedBy || "Owner",
        nextServiceMileage: initialMaintenance.nextServiceMileage ?? "",
        nextServiceDate: initialMaintenance.nextServiceDate || "",
        receiptUrl: initialMaintenance.receiptUrl || "",
        notes: initialMaintenance.notes || ""
      });
    } else {
      setForm({
        ...emptyForm,
        partsUsed: [createEmptyPart()]
      });
    }

    setFormError("");
  }, [isOpen, initialMaintenance]);

  if (!isOpen) return null;

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const updatePart = (partId, field, value) => {
    setForm((current) => ({
      ...current,
      partsUsed: current.partsUsed.map((part) =>
        part.id === partId ? { ...part, [field]: value } : part
      )
    }));
  };

  const addPart = () => {
    setForm((current) => ({
      ...current,
      partsUsed: [...current.partsUsed, createEmptyPart()]
    }));
  };

  const removePart = (partId) => {
    setForm((current) => ({
      ...current,
      partsUsed:
        current.partsUsed.length === 1
          ? [createEmptyPart()]
          : current.partsUsed.filter((part) => part.id !== partId)
    }));
  };

  const toNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    return Number(value);
  };

  const calculatePartsPreview = (parts = []) => {
    return parts.reduce((total, part) => {
      const cost = Number(part.cost);

      if (Number.isNaN(cost) || cost < 0) {
        return total;
      }

      return total + cost;
    }, 0);
  };

  const handleSave = () => {
    setFormError("");

    if (!form.serviceType) {
      setFormError("Choose a service type.");
      return;
    }

    if (!form.serviceDate) {
      setFormError("Enter a service date.");
      return;
    }

    if (form.mileage === "" || Number(form.mileage) < 0) {
      setFormError("Enter a valid mileage.");
      return;
    }

    const numericFields = [
      ["Labor cost", form.laborCost],
      ["Next service mileage", form.nextServiceMileage]
    ];

    for (const [label, value] of numericFields) {
      if (value !== "" && Number(value) < 0) {
        setFormError(`${label} cannot be negative.`);
        return;
      }
    }

    if (form.receiptUrl) {
      try {
        new URL(form.receiptUrl);
      } catch {
        setFormError("Enter a valid receipt URL.");
        return;
      }
    }

    const { partsUsed, errors } = normalizePartsUsed(
      form.partsUsed,
      form.serviceType
    );

    if (errors.length > 0) {
      setFormError(errors[0]);
      return;
    }

    const summedPartsCost = partsUsed.reduce(
      (total, part) => total + (part.cost || 0),
      0
    );

    const partsCost = summedPartsCost > 0 ? summedPartsCost : null;
    const laborCost = toNumberOrNull(form.laborCost);

    const totalCost =
      partsCost !== null && laborCost !== null
        ? partsCost + laborCost
        : partsCost !== null
        ? partsCost
        : laborCost !== null
        ? laborCost
        : null;

    const payload = {
      serviceType: form.serviceType,
      serviceDate: form.serviceDate,
      mileage: Number(form.mileage),
      partsUsed,
      totalCost,
      partsCost,
      laborCost,
      serviceProvider: form.serviceProvider.trim() || null,
      performedBy: form.performedBy,
      nextServiceMileage: toNumberOrNull(form.nextServiceMileage),
      nextServiceDate: form.nextServiceDate || null,
      receiptUrl: form.receiptUrl.trim() || null,
      notes: form.notes.trim() || null
    };

    onSave(payload);
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <h2 style={{ margin: 0 }}>
          {editingMaintenanceId ? "Edit Maintenance" : "Add Maintenance"}
        </h2>

        {formError && <div style={errorStyle}>{formError}</div>}

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Service Details</h3>

          <label style={labelStyle}>Service Type</label>
          <select
            value={form.serviceType}
            onChange={(e) => updateField("serviceType", e.target.value)}
            style={inputStyle}
          >
            {maintenanceServiceTypes.map((serviceType) => (
              <option key={serviceType} value={serviceType}>
                {serviceType}
              </option>
            ))}
          </select>

          <label style={labelStyle}>Service Date</label>
          <input
            type="date"
            value={form.serviceDate}
            onChange={(e) => updateField("serviceDate", e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Mileage</label>
          <input
            type="number"
            min="0"
            value={form.mileage}
            onChange={(e) => updateField("mileage", e.target.value)}
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Parts Used</h3>

          {form.partsUsed.map((part, index) => (
            <div key={part.id} style={partRowStyle}>
              <label style={labelStyle}>Part</label>
              <input
                placeholder="Example: Engine Oil, Oil Filter, Brake Pads"
                value={part.partName}
                onChange={(e) => updatePart(part.id, "partName", e.target.value)}
                style={inputStyle}
              />

              <label style={labelStyle}>Part Brand</label>
              <input
                placeholder="Example: Motul, Toyota, Brembo"
                value={part.brand}
                onChange={(e) => updatePart(part.id, "brand", e.target.value)}
                style={inputStyle}
              />

              <label style={labelStyle}>Part Number</label>
              <input
                placeholder="Manufacturer part number"
                value={part.partNumber}
                onChange={(e) =>
                  updatePart(part.id, "partNumber", e.target.value)
                }
                style={inputStyle}
              />

              <label style={labelStyle}>Part Cost</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={part.cost}
                onChange={(e) => updatePart(part.id, "cost", e.target.value)}
                style={inputStyle}
              />

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removePart(part.id)}
                  style={removeButtonStyle}
                >
                  Remove Part
                </button>
              )}
            </div>
          ))}

          <button type="button" onClick={addPart} style={secondaryButtonStyle}>
            Add Another Part
          </button>
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Cost</h3>

          <div style={summaryCostStyle}>
            <span>Parts Total</span>
            <strong>${calculatePartsPreview(form.partsUsed).toFixed(2)}</strong>
          </div>

          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Labor Cost - optional"
            value={form.laborCost}
            onChange={(e) => updateField("laborCost", e.target.value)}
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Provider</h3>

          <select
            value={form.performedBy}
            onChange={(e) => updateField("performedBy", e.target.value)}
            style={inputStyle}
          >
            {performedByOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <input
            placeholder="Shop, dealership, or service provider"
            value={form.serviceProvider}
            onChange={(e) => updateField("serviceProvider", e.target.value)}
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Follow-Up</h3>

          <input
            type="number"
            min="0"
            placeholder="Next Service Mileage - optional"
            value={form.nextServiceMileage}
            onChange={(e) => updateField("nextServiceMileage", e.target.value)}
            style={inputStyle}
          />

          <input
            type="date"
            value={form.nextServiceDate}
            onChange={(e) => updateField("nextServiceDate", e.target.value)}
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Documentation</h3>

          <input
            type="url"
            placeholder="Receipt or invoice link"
            value={form.receiptUrl}
            onChange={(e) => updateField("receiptUrl", e.target.value)}
            style={inputStyle}
          />

          <textarea
            placeholder="Notes - optional"
            value={form.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            style={{ ...inputStyle, height: "110px" }}
          />
        </section>

        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={onCancel}
            style={cancelButtonStyle}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            style={saveButtonStyle}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : editingMaintenanceId
              ? "Save Changes"
              : "Add Maintenance"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.82)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 30000,
  padding: "20px",
  boxSizing: "border-box",
  overflowY: "auto"
};

const modalStyle = {
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "20px",
  padding: "22px",
  maxWidth: "460px",
  width: "100%",
  maxHeight: "92vh",
  overflowY: "auto",
  boxSizing: "border-box",
  color: "white",
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const sectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const sectionTitleStyle = {
  margin: "4px 0",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  color: "#e10600"
};

const partRowStyle = {
  border: "1px solid #2a2a2a",
  borderRadius: "14px",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const inputStyle = {
  padding: "14px",
  borderRadius: "12px",
  border: "1px solid #2a2a2a",
  backgroundColor: "#1c1c1c",
  color: "white",
  boxSizing: "border-box",
  width: "100%",
  fontSize: "16px"
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: "600",
  color: "#fff"
};

const errorStyle = {
  backgroundColor: "#3a1111",
  border: "1px solid #7a1b1b",
  color: "white",
  borderRadius: "12px",
  padding: "12px",
  fontSize: "14px"
};

const summaryCostStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  padding: "14px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "16px"
};

const buttonRowStyle = {
  display: "flex",
  gap: "10px",
  marginTop: "4px"
};

const cancelButtonStyle = {
  flex: 1,
  backgroundColor: "#333",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "14px",
  fontWeight: "bold",
  cursor: "pointer"
};

const saveButtonStyle = {
  flex: 1,
  backgroundColor: "#e10600",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "14px",
  fontWeight: "bold",
  cursor: "pointer"
};

const secondaryButtonStyle = {
  backgroundColor: "#333",
  color: "white",
  border: "1px solid #444",
  borderRadius: "12px",
  padding: "12px",
  fontWeight: "bold",
  cursor: "pointer",
  fontSize: "16px"
};

const removeButtonStyle = {
  ...secondaryButtonStyle,
  backgroundColor: "#600",
  border: "none"
};

export default AddMaintenanceModal;